import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    CalendarDays, Plus, Link2, Copy, Trash2, Edit3, ExternalLink, Users, Clock, Globe,
    Code, Loader2, ToggleLeft, ToggleRight, X, ChevronRight, Video, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin; // Use frontend domain for public booking URLs

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function BookingLinks() {
    const { user, getAuthHeader } = useAuth();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editLink, setEditLink] = useState(null);
    const [showEmbedModal, setShowEmbedModal] = useState(null);
    const [showBookingsModal, setShowBookingsModal] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [editAttendeesFor, setEditAttendeesFor] = useState(null);
    const [orgUsers, setOrgUsers] = useState([]);
    const [selectedAttendees, setSelectedAttendees] = useState([]);
    const [savingAttendees, setSavingAttendees] = useState(false);

    const loadLinks = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/booking/links`, { headers: getAuthHeader() });
            setLinks(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { toast.error('Failed to load booking links'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { loadLinks(); }, [loadLinks]);

    const deleteLink = async (id) => {
        if (!window.confirm('Delete this booking link?')) return;
        try {
            await axios.delete(`${API}/booking/links/${id}`, { headers: getAuthHeader() });
            toast.success('Deleted');
            loadLinks();
        } catch { toast.error('Failed to delete'); }
    };

    const toggleActive = async (link) => {
        try {
            await axios.put(`${API}/booking/links/${link.booking_link_id}`, { is_active: !link.is_active }, { headers: getAuthHeader() });
            loadLinks();
        } catch { toast.error('Failed to toggle'); }
    };

    const viewBookings = async (link) => {
        setShowBookingsModal(link);
        try {
            const res = await axios.get(`${API}/booking/links/${link.booking_link_id}/bookings`, { headers: getAuthHeader() });
            setBookings(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { toast.error('Failed to load bookings'); }
        // Also load org users for attendee picker
        try {
            const res = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
            setOrgUsers(Array.isArray(res.data) ? res.data : []);
        } catch { /* ignore */ }
    };

    const openAttendeePicker = (booking) => {
        setEditAttendeesFor(booking);
        setSelectedAttendees(booking.additional_attendees || []);
    };

    const toggleAttendee = (userId) => {
        setSelectedAttendees(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const saveAttendees = async () => {
        if (!editAttendeesFor) return;
        setSavingAttendees(true);
        try {
            await axios.put(`${API}/booking/bookings/${editAttendeesFor.booking_id}/attendees`,
                { attendee_ids: selectedAttendees }, { headers: getAuthHeader() });
            toast.success('Team members updated');
            // Refresh bookings list
            setBookings(prev => prev.map(b =>
                b.booking_id === editAttendeesFor.booking_id
                    ? { ...b, additional_attendees: selectedAttendees }
                    : b
            ));
            setEditAttendeesFor(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally {
            setSavingAttendees(false);
        }
    };

    const copyLink = (slug) => {
        navigator.clipboard.writeText(`${BASE_URL}/book/${slug}`);
        toast.success('Booking link copied!');
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="booking-links-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Booking Links</h1>
                    <p className="text-sm text-stone-500 mt-1">Create scheduling pages for clients and prospects to book meetings</p>
                </div>
                <Button onClick={() => { setEditLink(null); setShowCreateModal(true); }} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="create-booking-link-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Booking Link
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
            ) : links.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <CalendarDays className="w-16 h-16 text-stone-300 mb-4" />
                    <h2 className="text-lg font-semibold text-stone-700 mb-2">No booking links yet</h2>
                    <p className="text-sm text-stone-500 mb-4 max-w-md">Create a booking link to let clients and prospects schedule meetings with you or your team.</p>
                    <Button onClick={() => setShowCreateModal(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-2" /> Create Your First Link
                    </Button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {links.map(link => (
                        <div key={link.booking_link_id} className={`bg-white border rounded-xl p-5 hover:shadow-sm transition-shadow ${!link.is_active ? 'opacity-60' : ''}`} data-testid={`booking-link-${link.booking_link_id}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: link.color + '15', color: link.color }}>
                                        {link.booking_type === 'round_robin' ? <Users className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-stone-900">{link.name}</h3>
                                        {link.description && <p className="text-sm text-stone-500 mt-0.5 line-clamp-1">{link.description}</p>}
                                        <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {link.duration} min</span>
                                            <span className="px-1.5 py-0.5 rounded-full bg-stone-100 font-medium">{link.booking_type === 'round_robin' ? 'Round Robin' : '1:1'}</span>
                                            <span className="flex items-center gap-1">
                                                {link.location === 'google_meet' || link.location === 'zoom' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                                                {link.location === 'google_meet' ? 'Google Meet' : link.location === 'zoom' ? 'Zoom' : link.location === 'phone' ? 'Phone' : link.location === 'in_person' ? 'In Person' : 'Custom'}
                                            </span>
                                            {link.booking_count > 0 && <span className="font-medium text-stone-700">{link.booking_count} bookings</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => toggleActive(link)} title={link.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-active-${link.booking_link_id}`}>
                                        {link.is_active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-stone-400" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => copyLink(link.slug)} data-testid={`copy-link-${link.booking_link_id}`}><Copy className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => window.open(`/book/${link.slug}`, '_blank')} data-testid={`preview-link-${link.booking_link_id}`}><ExternalLink className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => setShowEmbedModal(link)} data-testid={`embed-link-${link.booking_link_id}`}><Code className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => viewBookings(link)} data-testid={`bookings-link-${link.booking_link_id}`}><CalendarDays className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => { setEditLink(link); setShowCreateModal(true); }} data-testid={`edit-link-${link.booking_link_id}`}><Edit3 className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => deleteLink(link.booking_link_id)} className="text-red-500 hover:text-red-600" data-testid={`delete-link-${link.booking_link_id}`}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                                <code className="flex-1 text-xs font-mono bg-stone-50 border border-stone-200 rounded px-3 py-1.5 text-stone-600 truncate">{BASE_URL}/book/{link.slug}</code>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <BookingLinkModal
                open={showCreateModal}
                onClose={() => { setShowCreateModal(false); setEditLink(null); }}
                existing={editLink}
                getAuthHeader={getAuthHeader}
                onSaved={loadLinks}
            />

            {/* Embed Modal */}
            <EmbedModal open={!!showEmbedModal} onClose={() => setShowEmbedModal(null)} link={showEmbedModal} />

            {/* Bookings List Modal */}
            <Dialog open={!!showBookingsModal} onOpenChange={() => { setShowBookingsModal(null); setEditAttendeesFor(null); }}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bookings-list-modal">
                    <DialogHeader>
                        <DialogTitle>Bookings — {showBookingsModal?.name}</DialogTitle>
                        <DialogDescription>{bookings.length} total bookings</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        {bookings.length === 0 ? (
                            <p className="text-sm text-stone-500 py-8 text-center">No bookings yet</p>
                        ) : bookings.map(b => (
                            <div key={b.booking_id} className={`p-3 rounded-lg border text-sm ${b.status === 'cancelled' ? 'bg-red-50 border-red-100' : 'bg-stone-50 border-stone-100'}`} data-testid={`booking-${b.booking_id}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-stone-800">{b.guest_name} ({b.guest_email})</span>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{b.status}</span>
                                        {b.status === 'confirmed' && (
                                            <button onClick={() => openAttendeePicker(b)}
                                                className="flex items-center gap-1 text-xs text-stone-600 hover:text-stone-800 px-2 py-0.5 rounded border border-stone-200 hover:border-stone-300 bg-white transition-colors"
                                                data-testid={`add-attendees-${b.booking_id}`}>
                                                <Users className="w-3 h-3" />
                                                {(b.additional_attendees?.length || 0) > 0
                                                    ? `${b.additional_attendees.length} added`
                                                    : 'Add Team'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-xs text-stone-500 mt-1">
                                    {new Date(b.start_time).toLocaleDateString()} {new Date(b.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(b.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {(b.additional_attendees?.length || 0) > 0 && (
                                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                                        <span className="text-xs text-stone-400">Team:</span>
                                        {b.additional_attendees.map(uid => {
                                            const u = orgUsers.find(ou => ou.user_id === uid);
                                            return <span key={uid} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{u?.name || u?.email || uid}</span>;
                                        })}
                                    </div>
                                )}
                                {b.meet_link && <a href={b.meet_link} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline mt-1 inline-block">Join Meeting</a>}

                                {/* Inline Attendee Picker */}
                                {editAttendeesFor?.booking_id === b.booking_id && (
                                    <div className="mt-3 pt-3 border-t border-stone-200" data-testid="attendee-picker">
                                        <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Add Team Members to Meeting</p>
                                        <div className="max-h-36 overflow-y-auto space-y-1 border border-stone-200 rounded-lg p-2 bg-white">
                                            {orgUsers.filter(u => u.user_id !== b.assigned_to).map(u => (
                                                <label key={u.user_id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-stone-50 cursor-pointer text-sm">
                                                    <Checkbox
                                                        checked={selectedAttendees.includes(u.user_id)}
                                                        onCheckedChange={() => toggleAttendee(u.user_id)}
                                                        data-testid={`attendee-check-${u.user_id}`}
                                                    />
                                                    <span className="truncate">{u.name || u.email}</span>
                                                    <span className="text-xs text-stone-400 ml-auto">{u.role}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Button size="sm" onClick={saveAttendees} disabled={savingAttendees}
                                                className="bg-stone-900 text-white hover:bg-stone-800 text-xs h-7"
                                                data-testid="save-attendees-btn">
                                                {savingAttendees ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                                Save
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => setEditAttendeesFor(null)} className="text-xs h-7">Cancel</Button>
                                            <span className="text-xs text-stone-400 ml-auto">{selectedAttendees.length} selected — calendar invites will be sent</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ── Create/Edit Booking Link Modal ─────────────────────────────────────── */
function BookingLinkModal({ open, onClose, existing, getAuthHeader, onSaved }) {
    const [form, setForm] = useState({
        name: '', slug: '', description: '', duration: 30, buffer_time: 0,
        booking_type: 'one_on_one', location: 'google_meet', color: '#1a1a1a',
        max_days_ahead: 60, min_notice_hours: 1, confirmation_message: 'Your meeting has been scheduled!',
        availability: { days: [0, 1, 2, 3, 4], start_hour: 9, start_min: 0, end_hour: 17, end_min: 0 },
        assigned_users: [], custom_questions: [],
    });
    const [orgUsers, setOrgUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newQuestion, setNewQuestion] = useState('');

    useEffect(() => {
        if (open) {
            if (existing) {
                setForm({
                    name: existing.name || '',
                    slug: existing.slug || '',
                    description: existing.description || '',
                    duration: existing.duration || 30,
                    buffer_time: existing.buffer_time || 0,
                    booking_type: existing.booking_type || 'one_on_one',
                    location: existing.location || 'google_meet',
                    color: existing.color || '#1a1a1a',
                    max_days_ahead: existing.max_days_ahead || 60,
                    min_notice_hours: existing.min_notice_hours || 1,
                    confirmation_message: existing.confirmation_message || '',
                    availability: existing.availability || { days: [0, 1, 2, 3, 4], start_hour: 9, start_min: 0, end_hour: 17, end_min: 0 },
                    assigned_users: existing.assigned_users || [],
                    custom_questions: existing.custom_questions || [],
                });
            } else {
                setForm({
                    name: '', slug: '', description: '', duration: 30, buffer_time: 0,
                    booking_type: 'one_on_one', location: 'google_meet', color: '#1a1a1a',
                    max_days_ahead: 60, min_notice_hours: 1, confirmation_message: 'Your meeting has been scheduled!',
                    availability: { days: [0, 1, 2, 3, 4], start_hour: 9, start_min: 0, end_hour: 17, end_min: 0 },
                    assigned_users: [], custom_questions: [],
                });
            }
            // Load org users for assignment
            axios.get(`${API}/admin/users`, { headers: getAuthHeader() })
                .then(res => setOrgUsers(Array.isArray(res.data) ? res.data : []))
                .catch(() => setOrgUsers([]));
        }
    }, [open, existing, getAuthHeader]);

    const handleSubmit = async () => {
        if (!form.name) return toast.error('Name is required');
        setLoading(true);
        try {
            if (existing) {
                await axios.put(`${API}/booking/links/${existing.booking_link_id}`, form, { headers: getAuthHeader() });
                toast.success('Booking link updated');
            } else {
                await axios.post(`${API}/booking/links`, form, { headers: getAuthHeader() });
                toast.success('Booking link created');
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setLoading(false);
        }
    };

    const toggleDay = (day) => {
        const days = form.availability.days || [];
        setForm({
            ...form,
            availability: {
                ...form.availability,
                days: days.includes(day) ? days.filter(d => d !== day) : [...days, day].sort(),
            },
        });
    };

    const toggleUser = (userId) => {
        const current = form.assigned_users;
        const exists = current.find(u => u.user_id === userId);
        if (exists) {
            setForm({ ...form, assigned_users: current.filter(u => u.user_id !== userId) });
        } else {
            setForm({ ...form, assigned_users: [...current, { user_id: userId, weight: 1 }] });
        }
    };

    const updateWeight = (userId, weight) => {
        setForm({
            ...form,
            assigned_users: form.assigned_users.map(u =>
                u.user_id === userId ? { ...u, weight: Math.max(0, parseInt(weight) || 0) } : u
            ),
        });
    };

    const addQuestion = () => {
        if (!newQuestion.trim()) return;
        setForm({ ...form, custom_questions: [...form.custom_questions, { label: newQuestion.trim(), required: false }] });
        setNewQuestion('');
    };

    const removeQuestion = (idx) => {
        setForm({ ...form, custom_questions: form.custom_questions.filter((_, i) => i !== idx) });
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="booking-link-modal">
                <DialogHeader>
                    <DialogTitle>{existing ? 'Edit' : 'New'} Booking Link</DialogTitle>
                    <DialogDescription>Configure your scheduling page</DialogDescription>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Name</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="30 Min Meeting" className="mt-1" data-testid="bl-name" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">URL Slug</Label>
                            <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="30min-meeting" className="mt-1" data-testid="bl-slug" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase">Description</Label>
                        <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="A quick 30 min catch up..." rows={2} className="mt-1" data-testid="bl-description" />
                    </div>

                    {/* Duration, Buffer, Type */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Duration (min)</Label>
                            <Select value={String(form.duration)} onValueChange={v => setForm({ ...form, duration: parseInt(v) })}>
                                <SelectTrigger className="mt-1" data-testid="bl-duration"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[15, 30, 45, 60, 90, 120].map(d => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Buffer (min)</Label>
                            <Select value={String(form.buffer_time)} onValueChange={v => setForm({ ...form, buffer_time: parseInt(v) })}>
                                <SelectTrigger className="mt-1" data-testid="bl-buffer"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[0, 5, 10, 15, 30].map(b => <SelectItem key={b} value={String(b)}>{b === 0 ? 'None' : `${b} min`}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Type</Label>
                            <Select value={form.booking_type} onValueChange={v => setForm({ ...form, booking_type: v })}>
                                <SelectTrigger className="mt-1" data-testid="bl-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="one_on_one">1:1 Meeting</SelectItem>
                                    <SelectItem value="round_robin">Round Robin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Location</Label>
                            <Select value={form.location} onValueChange={v => setForm({ ...form, location: v })}>
                                <SelectTrigger className="mt-1" data-testid="bl-location"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="google_meet">Google Meet</SelectItem>
                                    <SelectItem value="zoom">Zoom</SelectItem>
                                    <SelectItem value="phone">Phone Call</SelectItem>
                                    <SelectItem value="in_person">In Person</SelectItem>
                                    <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Brand Color</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-10 h-10 rounded cursor-pointer border-0" data-testid="bl-color" />
                                <Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="flex-1" />
                            </div>
                        </div>
                    </div>

                    {/* Availability */}
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase mb-2 block">Available Days</Label>
                        <div className="flex gap-2">
                            {DAYS.map((d, i) => (
                                <button key={i} type="button" onClick={() => toggleDay(i)}
                                    className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${form.availability.days?.includes(i) ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-300'}`}
                                    data-testid={`bl-day-${i}`}>{d}</button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Start Time</Label>
                            <Input type="time" value={`${String(form.availability.start_hour || 9).padStart(2, '0')}:${String(form.availability.start_min || 0).padStart(2, '0')}`}
                                onChange={e => { const [h, m] = e.target.value.split(':'); setForm({ ...form, availability: { ...form.availability, start_hour: parseInt(h), start_min: parseInt(m) } }); }}
                                className="mt-1" data-testid="bl-start-time" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">End Time</Label>
                            <Input type="time" value={`${String(form.availability.end_hour || 17).padStart(2, '0')}:${String(form.availability.end_min || 0).padStart(2, '0')}`}
                                onChange={e => { const [h, m] = e.target.value.split(':'); setForm({ ...form, availability: { ...form.availability, end_hour: parseInt(h), end_min: parseInt(m) } }); }}
                                className="mt-1" data-testid="bl-end-time" />
                        </div>
                    </div>

                    {/* Assigned Users */}
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase mb-2 block">
                            {form.booking_type === 'round_robin' ? 'Round Robin Team Members' : 'Assigned To'}
                        </Label>
                        <div className="border border-stone-200 rounded-lg max-h-40 overflow-y-auto">
                            {orgUsers.map(u => {
                                const assigned = form.assigned_users.find(a => a.user_id === u.user_id);
                                return (
                                    <div key={u.user_id} className="flex items-center gap-3 px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-0">
                                        <Checkbox checked={!!assigned} onCheckedChange={() => toggleUser(u.user_id)} />
                                        <span className="text-sm flex-1 truncate">{u.name || u.email}</span>
                                        {u.calendar_connected && <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Cal</span>}
                                        {assigned && form.booking_type === 'round_robin' && (
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs text-stone-400">Weight:</span>
                                                <Input type="number" min="0" max="10" value={assigned.weight}
                                                    onChange={e => updateWeight(u.user_id, e.target.value)}
                                                    className="w-14 h-7 text-xs text-center" data-testid={`weight-${u.user_id}`} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {form.booking_type === 'round_robin' && (
                            <p className="text-xs text-stone-400 mt-1">Higher weight = more bookings assigned. Only users with Google Calendar connected can receive bookings.</p>
                        )}
                    </div>

                    {/* Custom Questions */}
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase mb-2 block">Custom Questions</Label>
                        {form.custom_questions.map((q, i) => (
                            <div key={i} className="flex items-center gap-2 mb-2">
                                <span className="text-sm flex-1 text-stone-700">{q.label}</span>
                                <label className="flex items-center gap-1 text-xs text-stone-500">
                                    <Checkbox checked={q.required} onCheckedChange={(v) => {
                                        const qs = [...form.custom_questions];
                                        qs[i] = { ...qs[i], required: v };
                                        setForm({ ...form, custom_questions: qs });
                                    }} /> Required
                                </label>
                                <button onClick={() => removeQuestion(i)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                            </div>
                        ))}
                        <div className="flex gap-2">
                            <Input value={newQuestion} onChange={e => setNewQuestion(e.target.value)} placeholder="e.g. What would you like to discuss?" className="flex-1 text-sm" onKeyDown={e => e.key === 'Enter' && addQuestion()} data-testid="bl-new-question" />
                            <Button variant="outline" size="sm" onClick={addQuestion} type="button">Add</Button>
                        </div>
                    </div>

                    {/* Advanced */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Max Days Ahead</Label>
                            <Input type="number" value={form.max_days_ahead} onChange={e => setForm({ ...form, max_days_ahead: parseInt(e.target.value) || 60 })} className="mt-1" data-testid="bl-max-days" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Min Notice (hours)</Label>
                            <Input type="number" value={form.min_notice_hours} onChange={e => setForm({ ...form, min_notice_hours: parseInt(e.target.value) || 1 })} className="mt-1" data-testid="bl-min-notice" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={loading} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="bl-submit">
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {existing ? 'Save Changes' : 'Create Booking Link'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ── Embed Code Modal ───────────────────────────────────────────────────── */
function EmbedModal({ open, onClose, link }) {
    if (!link) return null;
    const bookingUrl = `${BASE_URL}/book/${link.slug}`;
    const iframeCode = `<iframe src="${bookingUrl}?embed=true" width="100%" height="700" frameborder="0" style="border:none;border-radius:12px;"></iframe>`;
    const popupCode = `<script>
  (function(){
    var btn=document.createElement('button');
    btn.textContent='Book a Meeting';
    btn.style.cssText='background:${link.color};color:#fff;border:none;padding:12px 24px;border-radius:8px;font-size:14px;cursor:pointer;font-family:system-ui;';
    btn.onclick=function(){
      var d=document.createElement('div');
      d.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
      d.onclick=function(e){if(e.target===d)d.remove();};
      var f=document.createElement('iframe');
      f.src='${bookingUrl}?embed=true';
      f.style.cssText='width:480px;max-width:95vw;height:85vh;border:none;border-radius:16px;box-shadow:0 25px 50px rgba(0,0,0,0.25);';
      d.appendChild(f);
      document.body.appendChild(d);
    };
    document.currentScript.parentNode.insertBefore(btn,document.currentScript);
  })();
</script>`;

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-xl" data-testid="embed-modal">
                <DialogHeader>
                    <DialogTitle>Embed Codes — {link.name}</DialogTitle>
                    <DialogDescription>Add your booking page to any website</DialogDescription>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Inline Embed (iframe)</Label>
                            <Button variant="ghost" size="sm" onClick={() => copy(iframeCode)} className="text-xs h-7"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs font-mono text-stone-700 whitespace-pre-wrap overflow-x-auto" data-testid="embed-iframe-code">{iframeCode}</pre>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Popup Widget (button + modal)</Label>
                            <Button variant="ghost" size="sm" onClick={() => copy(popupCode)} className="text-xs h-7"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs font-mono text-stone-700 whitespace-pre-wrap overflow-x-auto max-h-48" data-testid="embed-popup-code">{popupCode}</pre>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Direct Link</Label>
                            <Button variant="ghost" size="sm" onClick={() => copy(bookingUrl)} className="text-xs h-7"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <code className="block bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs font-mono text-stone-700" data-testid="embed-direct-link">{bookingUrl}</code>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
